package dev.danko.roblox_craft_ai.uikit.i18n

import dev.danko.roblox_craft_ai.shared.model.AssetType

object UkStrings : AppStrings {
    override val common = object : CommonStrings {
        override val cancel = "Скасувати"
        override val save = "Зберегти"
        override val delete = "Видалити"
        override val retry = "Повторити"
        override val regenerate = "Згенерувати знову"
        override val close = "Закрити"
        override val clear = "Очистити"
        override val copy = "Копіювати"
        override val copied = "Скопійовано"
        override val confirm = "Підтвердити"
        override val send = "Надіслати"
        override val show = "Показати"
        override val hide = "Сховати"
        override val help = "Допомога"
        override val selectPlaceholder = "Обери..."
        override val demoBadge = "Демо"
        override val betaBadge = "Бета"
        override val you = "Ти"
        override val language = "Мова"
        override val simpleMode = "Простий режим"
        override val expertMode = "Режим експерта"
        override val simpleModeHint = "Простий режим приховує вибір AI-моделі та технічні деталі"
        override fun lines(count: Int) = "$count ${slavicPlural(count, "рядок", "рядки", "рядків")}"
        override fun secondsShort(seconds: Int) = "${seconds}с"
        override fun minutesSeconds(minutes: Int, seconds: Int) = "${minutes}хв ${seconds.toString().padStart(2, '0')}с"
        override fun agoSeconds(seconds: Int) = "${seconds}с тому"
        override fun agoMinutes(minutes: Int) = "${minutes}хв тому"
    }

    override val assetTypes = object : AssetTypeStrings {
        override fun name(type: AssetType) = when (type) {
            AssetType.LUAU_SCRIPT -> "Luau-скрипт"
            AssetType.MESH_3D -> "3D-модель"
            AssetType.ANIMATION -> "Анімація"
            AssetType.ROJO_PROJECT -> "Rojo-проєкт"
        }

        override fun generateButton(type: AssetType) = when (type) {
            AssetType.LUAU_SCRIPT -> "Створити скрипт"
            AssetType.MESH_3D -> "Створити 3D-модель"
            AssetType.ANIMATION -> "Створити анімацію"
            AssetType.ROJO_PROJECT -> "Створити Rojo-проєкт"
        }

        override fun availabilityNote(type: AssetType) = when (type) {
            AssetType.MESH_3D -> "Бета: 3D-модель будується за ескізом або твоєю картинкою (скріпка). У Studio модель поки вставляється заготовкою."
            AssetType.ANIMATION -> "Бета: ШІ створює анімацію для персонажа R15. Перед надсиланням у Studio виділи R15-модель — анімація відкриється в Animation Editor."
            else -> null
        }
    }

    override val errors = object : ErrorStrings {
        override val aiUnavailable = "AI зараз недоступний. Спробуй ще раз за хвилину."
        override val rateLimited = "Забагато запитів. Зачекай трохи й спробуй знову."
        override val network = "Немає зв'язку із сервером. Перевір, що бекенд запущено."
        override val meshTimeout = "3D-модель створювалася надто довго. Спробуй ще раз."
        override val meshFailed = "Не вдалося створити 3D-модель. Спробуй описати її інакше."
        override val cancelled = "Генерацію скасовано."
        override val generic = "Щось пішло не так. Спробуй ще раз."
        override val details = "Подробиці"
        override val backendDownTitle = "Сервер не запущено"
        override val backendDownHint = "Поки бекенд вимкнено, нічого створити не вийде. Запусти його в папці проєкту командою ./gradlew :backend:run — сторінка сама побачить, коли він запрацює."
        override val checkAgain = "Перевірити знову"
        override val contentBlocked = "Такий запит виконати не можна: він порушує правила для ігор у Roblox. Спробуй описати ідею інакше."
        override val unsafeCodeBlocked = "AI написав код, який порушує правила безпеки, тому ми його не видали. Спробуй переформулювати запит."
        override val authRequired = "Щоб створювати, увійди через email — це займе хвилину."
        override val quotaExceeded = "Кредити закінчилися. Купи пакет кредитів або обери тариф на сторінці «💎 Тарифи»."
        override val budgetExceeded = "Сьогодні ліміт генерацій вичерпано. Спробуй завтра."
    }

    override val promptBox = object : PromptBoxStrings {
        override val label = "Що створимо?"
        override val attachReference = "Прикріпити картинку"
        override fun addMorePhotos(count: Int) = "Додати картинку ($count)"
        override val placeholder = "Опиши, що потрібно створити для Roblox. Можна вставити картинку (Ctrl+V / Cmd+V)..."
        override val referencesLabel = "🖼️ Картинки-приклади:"
        override val assetTypeLabel = "Що створюємо"
        override val aiModelLabel = "AI-модель"
        override val autoProvider = "🤖 Авто"
        override val generating = "Створюю..."
        override val tooManyReferences = "⚠️ Можна не більше трьох картинок. Зайві не додано"
        override val viewsTitle = "Ракурси"
        override val viewFront = "Спереду"
        override val viewBack = "Ззаду"
        override val viewLeft = "Зліва"
        override val viewRight = "Справа"
        override val viewsHint = "Завантаж аркуш із кількома видами — розріжу його сам. Або додай види по одному в слоти. З 2 і більше видами модель будується одразу за ними, без ескізу."
        override val viewsSplitting = "Шукаю ракурси на картинці…"
        override fun viewsSplit(count: Int) = "Знайшов ракурсів: $count. Перевір, чи всі на своїх місцях"
        override val viewsSwapSides = "Поміняти ліво і право"
        override val viewsHighOnlyFront = "«Висока» (TRELLIS 2) бере лише вид спереду. Усі ракурси використовує «Звичайна» і «Максимальна»."
        override val viewsSwitchToMax = "Перемкнути на «Максимальну»"
        override val viewsTooMany = "⚠️ Слоти зайняті. Прибери картинку зі слота, щоб додати нову"
        override val onlyImages = "⚠️ Підходять лише PNG, JPG і WebP"
        override val fileTooLarge = "⚠️ Файл завеликий, максимум 5 МБ"
        override val dropTitle = "+ Перетягни сюди картинки-приклади"
        override val dropHint = "До 3 файлів, кожен до 5 МБ"
        override val dropAria = "Перетягніть сюди картинки-приклади, максимум три"
        override val emptyPrompt = "✏️ Спочатку опиши, що потрібно створити"
        override fun promptFromImages(type: AssetType) = "${assetTypes.name(type)} для Roblox за прикріпленими картинками"
    }

    override val assetCard = object : AssetCardStrings {
        override val stagePrompt = "Запит"
        override val stageAi = "AI"
        override val stageBuild = "Збирання"
        override val stageSync = "Перевірка"
        override val stageDone = "Готово"
        override val statusPending = "У черзі"
        override val statusValidating = "Перевірка"
        override val statusRetrying = "Виправлення"
        override val statusStreaming = "Створюється"
        override val statusReady = "Готово"
        override val statusFailed = "Помилка"
        override fun revisionBadge(current: Int, total: Int) = "v$current ($total ${slavicPlural(total, "версія", "версії", "версій")})"
        override fun referencesBadge(count: Int) = "🖼️ $count"
        override fun syncPending(countdown: String) = "🔗 Чекаємо Studio...$countdown"
        override fun syncDelivering(countdown: String) = "⚙ Надсилання...$countdown"
        override val syncSynced = "✅ Уже в Studio!"
        override val syncFailed = "❌ Не вдалося надіслати"
        override val deleteTooltip = "Видалити"
        override val waitingForPlugin = "🔗 Чекаємо, коли плагін Roblox Studio забере роботу..."
        override val progressValidating = "⚙ Перевіряю код..."
        override val progressRetrying = "⚠️ Знайшов помилку, виправляю..."
        override fun progressTokens(count: Int) = "⚡ Пишу код... ($count)"
        override val progressProcessing = "⏳ Працюю..."
        override val download = "📦 Завантажити: "
        override val editPrompt = "✏️ Змінити запит"
        override val aiChat = "Чат з AI"
        override val downloadZip = "Завантажити .ZIP"
        override val synced = "Надіслано!"
        override val retrySync = "Надіслати знову"
        override val syncToStudio = "У Roblox Studio"
        override val demoMeshHint = "Це демо-модель: справжня 3D-модель будується за ескізом або картинкою, коли на сервері підключено генератор 3D."
        override val betaAnimationHint = "Анімації в бета-версії: це демонстраційний результат."
    }

    override val pipeline = object : PipelineStrings {
        override val prompt = "Запит"
        override val luauPromptDescription = "Запит прийнято"
        override val llmStream = "AI пише код"
        override val llmStreamDescription = "Нейромережа пише Luau-код"
        override val syntaxCheck = "Перевірка"
        override val syntaxCheckDescription = "Перевірка синтаксису та безпеки коду"
        override val ready = "Готово"
        override val luauReadyDescription = "Скрипт можна надіслати в Studio"
        override val meshPromptDescription = "Опис 3D-моделі"
        override val meshGeneration = "3D-генерація"
        override val meshGenerationDescription = "Створення форми моделі"
        override val meshTexturing = "Текстури"
        override val meshTexturingDescription = "Розфарбування та матеріали"
        override val meshExport = "Експорт GLB"
        override val meshExportDescription = "Підготовка файлу моделі"
        override val meshReadyDescription = "3D-модель готова"
        override val animationPromptDescription = "Опис анімації"
        override val motionAi = "Рух"
        override val motionAiDescription = "Створення ключових кадрів"
        override val rigging = "Скелет R15"
        override val riggingDescription = "Прив'язка до кісток R15"
        override val animationReadyDescription = "Анімація готова (бета)"
        override val scaffoldTree = "Структура"
        override val scaffoldTreeDescription = "Створення файлів Rojo-проєкту"
        override val serverServices = "Сервер"
        override val serverServicesDescription = "Серверні сервіси"
        override val clientControllers = "Клієнт"
        override val clientControllersDescription = "Клієнтські контролери та UI"
        override val rojoReady = "Готово"
        override val rojoReadyDescription = "Проєкт готовий для Rojo"
    }

    override val chat = object : ChatStrings {
        override val quickOptimize = "⚡ Пришвидшити код"
        override val quickStrict = "🛡️ Увімкнути --!strict і типи"
        override val quickBugs = "🐛 Знайти помилки"
        override val quickErrors = "✨ Додати обробку помилок"
        override val quickDocs = "📝 Додати коментарі"
        override fun revisionShort(number: Int) = "Версія $number"
        override fun revisionLong(number: Int) = "Версія №$number"
        override val inputPlaceholder = "Напиши, що змінити або покращити в скрипті."
        override val aiAssistant = "🤖 AI-помічник"
        override val diffTitle = "Зміни (diff)"
        override val acceptChanges = "Прийняти зміни"
        override val thinking = "AI вивчає код і вносить зміни..."
        override val changesApplied = "✅ Зміни застосовано"
    }

    override val code = object : CodeStrings {
        override val run = "Запустити"
        override val runViaBackend = "Запустити через бекенд…"
        override val connectPluginFirst = "Спочатку підключи плагін Roblox Studio"
        override fun projectTree(files: Int) = "📁 Файли проєкту ($files)"
        override val filterPlaceholder = "Фільтр..."
        override val undoTooltip = "Скасувати останню дію"
        override val redoTooltip = "Повернути скасовану дію"
        override val resetTooltip = "Повернути початковий код"
        override val copyTooltip = "Скопіювати код"
        override val downloadTooltip = "Завантажити скрипт як .lua"
        override val fullscreenTooltip = "На весь екран"
    }

    override val meshViewer = object : MeshViewerStrings {
        override val shadingTextured = "Текстури"
        override val shadingClay = "Глина"
        override val shadingWire = "Сітка"
        override val shadingFlat = "Пласко"
        override val lightStudio = "Студія"
        override val lightSun = "Сонце"
        override val lightNeon = "Неон"
        override val cameraIso = "Ізо"
        override val cameraFront = "Спереду"
        override val cameraTop = "Згори"
        override val cameraSide = "Збоку"
        override val pause = "Пауза"
        override val spin = "Обертати"
        override val exitFullscreen = "Вийти"
        override val fullscreen = "На весь екран"
        override fun stats(triangles: String, vertices: String, dimensions: String) = "📊 $triangles трикут. • $vertices верш. • $dimensions"
        override val controlsHint = "ЛКМ: обертати • ПКМ/Shift: рухати • Коліщатко: масштаб"
        override val screenshot = "Скриншот"
        override val screenshotTitle = "Скриншот 3D"
        override fun screenshotSaved(filename: String) = "Скриншот моделі збережено: $filename"
        override val animationParseError = "Не вдалося прочитати анімацію, персонаж стоїть у базовій позі"
        override val animationMissing = "У цієї роботи немає кадрів (стара демо-версія) — створи анімацію заново"
        override val animationPreviewTitle = "Перегляд анімації"
        override val play = "Грати"
        override val stop = "Стоп"
        override val loopOn = "Повтор: УВІМК"
        override val loopOff = "Повтор: ВИМК"
        override val exportKeyframes = "Експорт кадрів (.json)"
        override fun resizeHint(heightPx: Int) = "↕ Потягни, щоб змінити висоту (${heightPx}px)"
        override val shadingBaseColor = "Лише колір"
        override val shadingNormals = "Нормалі"
        override val shadingUvChecker = "Шахівниця UV"
        override val shadingXray = "Рентген"
        override val lightRoblox = "Roblox"
        override val lightNight = "Ніч"
    }

    override val header = object : HeaderStrings {
        override val appTitle = "Craftiva AI"
        override val appSubtitle = "Майстерня Roblox"
        override val presets = "📚 Ідеї"
        override val settings = "Налаштування"
        override fun aiReady(count: Int) = "AI: підключено $count"
        override val aiDemo = "AI: демо-режим"
        override val pluginConnected = "Плагін: підключено"
        override val pluginPending = "Плагін: чекаємо"
        override val tokenLabel = "Токен: "
    }

    override val home = object : HomeStrings {
        override val searchWorks = "Знайти роботу"
        override val allIdeas = "Усі ідеї →"
        override val open = "Відкрити"
        override val moreActions = "Інші дії"
        override val generateSimilar = "Схоже"
        override val options = "Параметри"
        override val sendToStudio = "Надіслати в Roblox Studio"
        override fun creditsCost(credits: Int) = "$credits " + slavicPlural(credits, "кредит", "кредити", "кредитів")
        override val rename = "Перейменувати"
        override val renameFailed = "Не вдалося перейменувати"
        override val noMatches = "Нічого не знайдено"
        override val pageTitle = "Craftiva AI — майстерня Roblox"
        override val heroTitle = "Що створимо сьогодні?"
        override val heroSubtitle = "Опиши ідею — отримаєш готовий скрипт або модель і надішлеш у Roblox Studio однією кнопкою."
        override val samplePrompt = "Зроби дерев'яний меч"
        override val savePreset = "💾 Зберегти як ідею"
        override val addToQueue = "✚ У чергу"
        override val conceptMode = " 🎨 Спочатку малюнок, потім 3D"
        override val shiftHint = "💡 Shift + «Створити» = додати в чергу"
        override val noIdea = "💡 Не знаєш, що створити?"
        override val browsePresets = "📚 Переглянути ідеї"
        override val recentAssets = "Мої роботи"
        override fun totalAssets(count: Int) = "(усього $count)"
        override val wideView = "Широко"
        override val gridView = "Сіткою"
        override val noAssetsTitle = "Поки порожньо"
        override val noAssetsHint = "Напиши запит вище, щоб створити першу роботу."
        override fun generationFailed(reason: String) = "Помилка генерації: $reason"
        override val cancelledByUser = "Генерацію скасовано"
        override val conceptGenerating = "🎨 Малюю ескіз..."
        override val conceptReady = "✨ Ескіз готовий! Подивись на форму перед 3D."
        override fun conceptFailed(reason: String) = "Помилка ескізу: $reason"
        override val conceptApproved = "🚀 Ескіз схвалено! Створюю 3D-модель..."
        override fun meshFromConceptFailed(reason: String) = "Помилка 3D-моделі: $reason"
        override val conceptRerolling = "🔄 Малюю інший варіант..."
        override val conceptRerolled = "✨ Новий варіант готовий!"
        override fun conceptRerollFailed(reason: String) = "Не вдалося перемалювати: $reason"
        override fun presetLoaded(title: String) = "✨ Ідею «$title» завантажено"
        override fun queued(pending: Int, position: Int) = "✨ У черзі: $pending, позиція №$position"
        override val enterPromptBeforeSaving = "⚠️ Спочатку напиши запит"
        override val personalPresetDescription = "Моя ідея"
        override fun presetSaved(title: String) = "💾 Ідею «$title» збережено. Знайди її в 📚 Ідеї → 👤 Мої"
        override val presetSaveFailed = "Не вдалося зберегти"
        override fun cardDeleted(shortId: String) = "🗑️ Роботу #$shortId видалено"
        override val regenerating = "🔄 Створюю заново..."
        override fun queuedForStudio(shortId: String) = "Роботу #$shortId надіслано в чергу Studio!"
        override val syncBackendUnavailable = "Не вдалося зв'язатися із сервером для надсилання."
        override val settingsSaved = "Налаштування збережено!"
        override fun refactorFailed(reason: String) = "Помилка під час зміни коду: $reason"
        override val changesApplied = "Зміни застосовано!"
        override fun applyChangesFailed(reason: String) = "Не вдалося застосувати зміни: $reason"
    }

    override val presetDialog = object : PresetDialogStrings {
        override val title = "💾 Зберегти як свою ідею"
        override val nameLabel = "Назва"
        override val namePlaceholder = "Мій крутий вогняний ефект (3–60 символів)"
        override val nameTooShort = "Потрібно хоча б 3 символи"
        override fun nameCounter(length: Int) = "OK · $length/60"
        override val descriptionLabel = "Опис (необов'язково)"
        override val descriptionPlaceholder = "Коротко, для чого цей запит (до 140 символів)"
        override val tagsLabel = "Теги (необов'язково)"
        override val tagsPlaceholder = "вогонь, магія, меч — через кому"
        override fun tagsCounter(count: Int) = "До 6 тегів · $count"
        override val currentPrompt = "Поточний запит (буде збережено):"
        override val emptyPrompt = "(порожньо — спочатку напиши запит)"
        override fun typeAndLimit(typeName: String) = "Тип: $typeName · максимум 50 своїх ідей"
    }

    override val queue = object : QueueStrings {
        override val statusPending = "Чекає"
        override val statusRunning = "У роботі"
        override val statusDone = "Готово"
        override val statusFailed = "Помилка"
        override val statusCancelled = "Скасовано"
        override fun title(size: Int) = "📦 Черга ($size)"
        override fun eta(minutes: Int, seconds: Int) = " ⏱️ Залишилось ~${minutes}хв ${seconds.toString().padStart(2, '0')}с"
        override val resume = "▶️ Продовжити"
        override val pause = "⏸️ Пауза"
        override val resumed = "Чергу продовжено"
        override val paused = "Черга на паузі"
        override val pausedBadge = " ⏸️ На паузі"
        override val cleared = "Готові завдання прибрано"
        override val empty = "🕳️ Поки немає завдань. Shift + «Створити» додасть завдання в чергу."
        override val scrollTip = "💡 Якщо завдань більше 30, список прокручується"
        override val taskCancelled = "Завдання скасовано"
        override fun position(position: Int) = "позиція $position"
        override val startCallbackMissing = "Черга ще не готова"
        override val generationFailed = "Помилка генерації"
        override val unexpectedError = "Неочікувана помилка"
    }

    override val concept = object : ConceptStrings {
        override val stepTitle = "1. Ескіз (2D)"
        override val stepHint = "Перевір форму й силует перед створенням 3D-моделі"
        override val savingBadge = "💎 Економить 3D-генерації"
        override val imageAlt = "Ескіз"
        override fun promptExpansion(expanded: Boolean) = "🔍 Докладний запит для художника (${if (expanded) "згорнути" else "показати"})"
        override val reconstructing = "⏳ Створюю 3D..."
        override val approve = "✅ Подобається, роби 3D"
        override val regenerating = "↻ Малюю..."
        override val regenerate = "🔄 Намалювати інший"
    }

    override val health = object : HealthStrings {
        override val unknown = "⚪ Стан невідомий"
        override val allOk = "🌿 Усі системи в порядку"
        override val critical = "🔴 Критична помилка"
        override fun degraded(component: String) = "🟡 Проблеми: $component"
        override val degradedGeneric = "🟡 Є проблеми"
        override val title = "Стан системи"
        override fun updatedAt(time: String) = "Оновлено: $time"
    }

    override val sessionToken = object : SessionTokenStrings {
        override val validationFailed = "Не вдалося перевірити токен. Переконайся, що він збігається з токеном у плагіні Roblox Studio."
        override val title = "🔗 Підключити плагін Roblox Studio"
        override val saveAndLink = "Зберегти й підключити"
        override val instructions = "💡 Скопіюй токен цього сайту й встав його у вікно плагіна Craftiva у Roblox Studio — так твої роботи потраплятимуть просто в Studio."
        override val tokenLabel = "Токен сесії"
        override val tokenPlaceholder = "наприклад RK_LIVE_7AB3C9EF"
        override val pasteFromClipboard = "Вставити з буфера"
        override val generateNew = "Створити новий"
    }

    override val syncModal = object : SyncModalStrings {
        override val untitledAsset = "(без назви)"
        override val title = "⚠️ Надіслати в Roblox Studio?"
        override val confirm = "Так, надіслати"
        override val warning = "⚠️ Робота з'явиться у твоєму Roblox Studio під час наступної синхронізації плагіна (кожні 3 секунди). Скрипти вставляються вимкненими — перевір код і натисни «Увімкнути» у вікні плагіна. Усе можна скасувати через Ctrl+Z."
        override val averageDelay = "⏱️ Зазвичай це займає 2–3 секунди."
        override val dontShowAgain = "Більше не питати (не рекомендовано)"
    }

    override val revisions = object : RevisionStrings {
        override val title = "👁️ Код та історія версій"
        override val listLabel = "Версії:"
        override fun restore(number: Int) = "Повернути v$number"
        override fun restored(number: Int) = "Повернуто версію v$number"
        override fun latest(number: Int) = "Остання (v$number)"
    }

    override val settingsRedirect = object : SettingsRedirectStrings {
        override val deprecatedNotice = "Використовуй сторінку /settings замість цього вікна."
        override val redirecting = "Перехід..."
        override val openingNewPage = "Відкривається сторінка налаштувань"
        override val movedNotice = "Налаштування переїхали на окрему сторінку /settings"
        override val openSettings = "Відкрити налаштування →"
    }

    override val settingsPage = object : SettingsPageStrings {
        override val tabAccount = "👤 Акаунт"
        override val signedOutTitle = "Ти не увійшов в акаунт"
        override val signedOutHint = "Увійди через email, щоб бачити тариф і кредити та користуватися сервісом з різних пристроїв."
        override val advanced = "Додатково"
        override val dangerZone = "Скидання"
        override val resetSessionHint = "Видаляє всі ключі та налаштування цієї сесії. Роботи на сервері залишаться."
        override val pageTitle = "Craftiva AI — Налаштування"
        override val subtitle = "Панель налаштувань"
        override val hashLabel = "Хеш: "
        override val backToStudio = "← До майстерні"
        override val cabinetTitle = "⚙️ Налаштування"
        override val cabinetSubtitle = "Акаунт, плагін Roblox Studio та вподобання"
        override val resetSession = "Скинути сесію"
        override val resetSessionConfirm = "Видалити всі ключі та налаштування цієї сесії?"
        override val sessionReset = "Сесію скинуто"
        override val tabKeys = "🔑 API-ключі"
        override val tabGeneral = "⚙️ Загальні"
        override val tabPlugin = "🔌 Плагін"
        override val tabAssets = "📦 Роботи"
        override val keyAdded = "Ключ додано ✓"
        override val keyAddFailed = "Не вдалося додати ключ"
        override val keyUpdated = "Ключ оновлено"
        override val keyUpdateFailed = "Не вдалося оновити ключ"
        override val keyDeleted = "Ключ видалено"
        override val keyDeleteFailed = "Не вдалося видалити ключ"
        override val priorityChanged = "Пріоритет змінено"
        override val priorityChangeFailed = "Не вдалося змінити пріоритет"
        override val settingsSaved = "Налаштування збережено ✓"
        override val saveFailed = "Не вдалося зберегти"
        override val resetDone = "Налаштування скинуто"
        override val resetFailed = "Не вдалося скинути"
        override val pluginSettingsSaved = "Налаштування плагіна збережено"
        override val connectionOkEmpty = "З'єднання є (черга порожня)"
        override fun connectionOkQueued(count: Int) = "З'єднання є (у черзі: $count)"
        override val connectionFailed = "Не вдалося зв'язатися із сервером синхронізації"
        override val assetRemovedFromCache = "Роботу видалено з кешу браузера"
        override val keysTitle = "Керування API-ключами"
        override val keysManagedByServerTitle = "🔐 Ключі керуються на сервері"
        override val keysManagedByServerHint = "Ключі провайдерів задає адміністратор сервера через змінні середовища або секрети. Змінити їх з інтерфейсу не можна."
        override fun keysSummary(total: Int, active: Int) = "Усього ключів: $total · Активних: $active"
        override val refresh = "Оновити"
        override val catalogMissing = "Каталог провайдерів не завантажено"
        override val catalogMissingHint = "Перевір, що бекенд запущено й він доступний"
        override val providersHeader = "🤖 AI- та 3D-провайдери"
        override val noModels = "(немає доступних моделей — додай ключ)"
        override val generalTitle = "Загальні налаштування"
        override val defaultModel = "AI-модель за замовчуванням"
        override val selectModel = "Обери модель..."
        override val defaultAssetType = "Тип роботи за замовчуванням"
        override val skipSyncConfirm = "Не питати перед надсиланням у Studio"
        override val skipSyncConfirmHint = "Готові роботи одразу надсилатимуться в Roblox Studio"
        override val themeSystem = "🌓 Як у системі"
        override val themeLight = "☀️ Світла"
        override val themeDark = "🌙 Темна"
        override val themeLabel = "Тема"
        override val languageLabel = "Мова інтерфейсу"
        override val reset = "Скинути"
        override val pluginSettingsTitle = "Налаштування плагіна Roblox Studio"
        override val pluginTokenTitle = "Токен сесії для плагіна"
        override val tokenCopied = "Токен скопійовано"
        override val sessionHashTitle = "Хеш сесії (ID сховища на бекенді)"
        override val pluginPort = "Порт плагіна"
        override val pluginPortHint = "Адреса сервера задається у вікні плагіна в Roblox Studio"
        override val autoConnect = "Підключатися автоматично під час запуску"
        override val autoConnectHint = "Плагін почне синхронізацію одразу після відкриття Studio"
        override val studioPath = "Шлях встановлення Roblox Studio (необов'язково)"
        override val testConnection = "Перевірити з'єднання"
        override val howToConnectTitle = "📚 Як підключити плагін?"
        override val howToConnectSteps = listOf(
            "1. Завантаж плагін кнопкою нижче — адресу сервера в ньому вже вказано.",
            "2. Поклади файл у папку плагінів Roblox Studio (у Studio: Plugins → Plugins Folder) і перезапусти Studio.",
            "3. Відкрий панель Craftiva AI, встав токен сесії зверху й натисни «Зберегти й підключити». Дозволь Studio доступ до сервера, коли з'явиться запит.",
            "4. Готові роботи з'являться в Studio вимкненими: перевір код і натисни «Увімкнути». Для анімації спершу виділи R15-модель. Ctrl+Z скасовує вставку."
        )
        override val downloadPlugin = "Завантажити плагін"
        override val myAssetsTitle = "Мої роботи (кеш браузера)"
        override fun assetsSummary(total: Int, shown: Int) = "Усього: $total · Показано: $shown"
        override val filterAll = "Усі"
        override val filterReady = "Готові"
        override val filterProcessing = "У роботі"
        override val filterFailed = "Помилки"
        override val deselectAll = "Зняти все"
        override val selectAll = "Вибрати все"
        override fun export(selected: Int) = if (selected > 0) "Експорт ($selected)" else "Експорт усіх"
        override fun deleteSelected(count: Int) = "Видалити ($count)"
        override fun deleteAssetsConfirm(count: Int) = "Видалити з кешу робіт: $count?"
        override val deleted = "Видалено"
        override val noAssets = "Робіт поки немає"
        override val noAssetsHint = "Створи першу роботу на головній сторінці"
        override val goToStudio = "Перейти до майстерні →"
    }

    override val providerCard = object : ProviderCardStrings {
        override val defaultLabel = "Основний"
        override fun type(name: String) = "Тип: $name"
        override val active = "✓ Активний"
        override val notConfigured = "Не налаштовано"
        override fun addKeyFor(provider: String) = "+ Додати ключ для $provider"
        override fun addKeyTitle(provider: String) = "Додати ключ $provider"
        override val labelField = "Назва ключа"
        override val labelPlaceholder = "Основний, Робочий тощо"
        override val apiKeyField = "API-ключ"
        override val apiKeyPlaceholder = "Встав API-ключ..."
        override fun keyPatternWarning(provider: String) = "⚠️ Ключ $provider зазвичай виглядає інакше"
        override val keyTooShort = "Ключ закороткий (мінімум 8 символів)"
        override val baseUrlOptional = "Base URL (необов'язково)"
        override fun baseUrlDefault(url: String) = "За замовчуванням: $url"
        override val test = "Перевірити"
        override val getApiKey = "Отримати API-ключ ↗"
        override fun editKeyTitle(provider: String) = "Змінити ключ $provider"
        override val labelShort = "Назва"
        override val baseUrlEmptyDefault = "Base URL (порожньо = за замовчуванням)"
        override val keyHiddenNotice = "🔒 Ключ збережено, і він не показується. Щоб замінити його, створи новий ключ."
        override fun deleteKeyTitle(label: String) = "Видалити ключ «$label»?"
        override val deleteIrreversible = "Цю дію не можна скасувати. Ключ буде назавжди видалено з налаштувань."
        override fun providerKeyLine(provider: String, key: String) = "⚠️ Провайдер: $provider · Ключ: $key"
        override fun secondsLeft(seconds: Int) = "(${seconds}с)"
    }

    override val presetsPage = object : PresetsPageStrings {
        override val pageTitle = "Craftiva AI — Ідеї"
        override val subtitle = "📚 Бібліотека ідей"
        override val toStudio = "🎮 До майстерні"
        override val categories = "Категорії"
        override val categoryAll = "Усі"
        override val categoryEnvironment = "Оточення"
        override val categoryNpc = "Персонажі та NPC"
        override val categoryInterior = "Інтер'єр і меблі"
        override val categoryWeapon = "Зброя та інструменти"
        override val categoryItem = "Предмети"
        override val categoryAnimation = "Анімації та ефекти"
        override val categoryScript = "Скрипти"
        override val categoryPersonal = "Мої"
        override val categoryOther = "Інше"
        override val allCategories = "Усі категорії"
        override val difficultyEasy = "Легко"
        override val difficultyMedium = "Середньо"
        override val difficultyHard = "Складно"
        override val searchPlaceholder = "🔍 Пошук за назвою, описом, тегами..."
        override fun found(count: Int) = "Знайдено: $count"
        override val noPersonalTitle = "У тебе поки немає своїх ідей"
        override val noPersonalHint = "Збережи вдалий запит на головній сторінці — кнопка «💾 Зберегти як ідею»."
        override val goToStudio = "🎮 Перейти до майстерні"
        override fun nothingFound(query: String) = "Нічого не знайдено за запитом «$query»"
        override val nothingFoundHint = "Спробуй інший запит або обери іншу категорію."
        override val deleteTitle = "Видалити ідею?"
        override fun deleted(title: String) = "Ідею «$title» видалено"
        override fun deleteConfirm(title: String) = "Ідею «$title» буде видалено назавжди. Продовжити?"
        override val usePrompt = "Використати"
    }

    override val nav = object : NavStrings {
        override val menu = "Меню"
        override val on = "Увімк"
        override val off = "Вимк"
        override fun plan(name: String) = "Тариф $name"
        override val studioConnected = "Плагін Roblox Studio підключено"
        override val studioNotConnected = "Плагін Roblox Studio не підключено"
        override val workshop = "🎮 Майстерня"
        override val home = "Головна"
        override val about = "Про проєкт"
        override val toggleColorMode = "Змінити тему"
        override val builtWith = "Зроблено на "
        override val templateBy = ", шаблон від "
    }

    override val report = object : ReportStrings {
        override val button = "Поскаржитися"
        override val title = "🚩 Поскаржитися на роботу"
        override val hint = "Розкажи, що не так. Скаргу побачить адміністратор."
        override val reasonLabel = "Причина"
        override val reasonInappropriate = "Неприйнятний вміст"
        override val reasonUnsafeCode = "Небезпечний або шкідливий код"
        override val reasonCopyright = "Порушення авторських прав"
        override val reasonOther = "Інше"
        override val commentLabel = "Коментар (необов'язково)"
        override val commentPlaceholder = "Що саме не так?"
        override val submit = "Надіслати скаргу"
        override val thanks = "Дякуємо! Скаргу надіслано."
        override val failed = "Не вдалося надіслати скаргу. Спробуй пізніше."
    }

    override val auth = object : AuthStrings {
        override val signIn = "Увійти"
        override val signOut = "Вийти"
        override val signedOut = "Ти вийшов з акаунта"
        override val accountTooltip = "Акаунт"
        override val dialogTitle = "Вхід через email"
        override val dialogHint = "Ми надішлемо лист із посиланням для входу. Пароль не потрібен."
        override val emailLabel = "Email"
        override val emailPlaceholder = "you@example.com"
        override val sendLink = "Надіслати посилання"
        override val sentTitle = "📬 Перевір пошту"
        override fun sentHint(email: String) = "Ми надіслали посилання на $email. Воно діє 15 хвилин. Якщо листа немає, зазирни в «Спам»."
        override val useAnotherEmail = "Інший email"
        override val invalidEmail = "Перевір адресу: схоже, в ній помилка."
        override val throttled = "Забагато листів. Зачекай 15 хвилин і спробуй знову."
        override val sendFailed = "Не вдалося надіслати лист. Спробуй пізніше."
        override val verifyTitle = "Вхід до Craftiva AI"
        override val verifying = "Перевіряю посилання…"
        override fun verifySuccess(email: String) = "Готово! Ти увійшов як $email."
        override val verifyFailed = "Посилання не працює: воно застаріло або вже використане."
        override val requestNewLink = "Запросити нове посилання"
        override val toWorkshop = "До майстерні →"
        override fun creditsTooltip(left: Int, limit: Int, purchased: Int, resetsOn: String) = "Місячних: $left з $limit (оновляться $resetsOn). Куплених: $purchased"
    }

    override val billing = object : BillingStrings {
        override val navPricing = "💎 Тарифи"
        override val pageTitle = "Craftiva AI — Тарифи"
        override val title = "Тарифи та кредити"
        override val subtitle = "Кожна дія коштує кредити: скрипт — 1, 3D-модель — 10. Місячні кредити оновлюються щомісяця, куплені не згорають."
        override val perMonth = "/міс"
        override fun creditsPerMonth(credits: Int) = "$credits кредитів на місяць"
        override val unlimitedCredits = "Без обмежень"
        override val currentPlan = "Твій тариф"
        override val subscribe = "Оформити"
        override val switchPlan = "Перейти"
        override val free = "Безкоштовно"
        override val packsTitle = "Пакети кредитів"
        override val packsHint = "Разова покупка. Кредити не згорають і витрачаються після місячних."
        override fun packCredits(credits: Int) = "$credits кредитів"
        override val buy = "Купити"
        override val manageTitle = "Керування підпискою"
        override val cancelSubscription = "Скасувати підписку"
        override val updatePayment = "Змінити спосіб оплати"
        override fun renewsOn(date: String) = "Продовжиться $date"
        override val pastDueHint = "Не вдалося списати оплату. Онови спосіб оплати, щоб тариф не вимкнувся."
        override val paymentSoon = "Оплата скоро з’явиться"
        override val signInToBuy = "Увійди, щоб купити"
        override val checkoutDone = "Оплата пройшла! Тариф і кредити оновляться за кілька секунд."
        override val checkoutFailed = "Не вдалося відкрити оплату. Спробуй пізніше."
        override val creditCostsTitle = "Скільки коштують дії"
        override fun balance(monthlyLeft: String, purchased: Int) = "Залишилось: $monthlyLeft місячних + $purchased куплених"
    }

    override val admin = object : AdminStrings {
        override val nav = "🛠 Адмінка"
        override val pageTitle = "Craftiva AI — Адмінка"
        override val title = "Адмінка"
        override val tabOverview = "📊 Огляд"
        override val tabPlans = "💳 Тарифи"
        override val tabPacks = "⚡ Пакети"
        override val tabUsers = "👥 Користувачі"
        override val tabModeration = "🛡️ Модерація"
        override val tabAudit = "📜 Журнал"
        override val days7 = "7 днів"
        override val days30 = "30 днів"
        override val totalCost = "Витрати всього"
        override val anonymousCost = "Без входу"
        override val topUsers = "Найбільш витратні користувачі"
        override val byDay = "По днях"
        override val events = "подій"
        override val noData = "Поки немає даних"
        override val name = "Назва"
        override val monthlyCredits = "Кредитів на місяць"
        override val credits = "Кредитів"
        override val price = "Ціна (центи)"
        override val currency = "Валюта"
        override val paddlePriceId = "Paddle Price ID"
        override val active = "Активний"
        override val unlimitedHint = "Порожньо — без ліміту"
        override val save = "Зберегти"
        override val saved = "Збережено"
        override val priceWarning = "Сума списання задається ціною в Paddle. Змінюючи ціну, створіть нову ціну в Paddle і впишіть її Price ID сюди."
        override val addPack = "Додати пакет"
        override val packId = "ID пакета (a-z, 0-9, _)"
        override val searchUsers = "Пошук за email…"
        override val email = "Email"
        override val plan = "Тариф"
        override val creditsMonth = "Кредити за місяць"
        override val purchased = "Куплені"
        override val cost30d = "$ за 30 днів"
        override val status = "Статус"
        override val registered = "Реєстрація"
        override val lastLogin = "Останній вхід"
        override val creditsDelta = "Кредити (+ нарахувати / − списати)"
        override val note = "Коментар"
        override val grant = "Застосувати"
        override val granted = "Кредити змінено"
        override val block = "Заблокувати"
        override val unblock = "Розблокувати"
        override val statusChanged = "Статус змінено"
        override val noUsers = "Користувачів не знайдено"
        override val onlyOpen = "Лише відкриті"
        override val resolve = "Розглянуто"
        override val resolved = "Позначено як розглянуте"
        override val kind = "Тип"
        override val category = "Категорія"
        override val excerpt = "Фрагмент"
        override val noIncidents = "Інцидентів немає"
        override val whenLabel = "Коли"
        override val admin = "Хто"
        override val action = "Дія"
        override val target = "Об’єкт"
        override val day = "День"
        override val user = "Користувач"
        override val cost = "Витрати"
        override val providers = "Провайдери"
        override val details = "Подробиці"
        override val totalEvents = "Усього подій"
        override fun saveFailed(reason: String) = "Не вдалося зберегти: $reason"
    }

    override val panel = object : AssetPanelStrings {
        override val tabCode = "Код"
        override val tabChat = "Чат з AI"
        override val tabVersions = "Версії"
        override val tabRequest = "Запит"
        override val tabFiles = "Файли"
        override val tab3d = "3D"
        override val tabPrep = "Скрипт підготовки"
        override val tabSketch = "Ескіз"
        override val tabAnimation = "Анімація"
        override val downloadGlb = "Завантажити .glb"
        override val downloadLua = "Завантажити .lua"
        override val validationOk = "Перевірено: помилок немає"
        override val technical = "Технічні деталі"
        override val whatToChange = "Що змінити? Наприклад: зачиняти двері через 3 секунди"
        override val change = "Змінити"
        override val currentVersion = "Поточна"
        override val references = "Картинки-приклади"
        override val noVersions = "Поки одна версія. Нові з’являються після правок у чаті."
        override fun version(n: Int) = "версія $n"
        override fun validationIssues(n: Int) = "Зауважень: $n"
    }

    override val tour = object : TourStrings {
        override val step1Title = "Опиши ідею"
        override val step1Text = "Напиши, що потрібно: скрипт, 3D-модель чи анімацію. Можна прикріпити картинку-приклад скріпкою."
        override val step2Title = "Отримай результат"
        override val step2Text = "AI створить роботу за кілька секунд. Відкрий її, щоб подивитися код чи модель і попросити правки."
        override val step3Title = "Надішли в Roblox Studio"
        override val step3Text = "Підключи плагін у налаштуваннях — і готові роботи з’являтимуться в Studio однією кнопкою."
        override val next = "Далі"
        override val back = "Назад"
        override val start = "Почати"
        override val skip = "Пропустити"
        override fun stepOf(step: Int, total: Int) = "Крок $step з $total"
    }

    override val legal = object : LegalStrings {
        override val terms = "Умови використання"
        override val privacy = "Конфіденційність"
        override val refunds = "Повернення"
        override val notAffiliated = "Не пов'язано з Roblox Corporation. Roblox — торговельна марка Roblox Corporation."
        override val loginConsent = "Входячи, ви приймаєте Умови використання та Політику конфіденційності. Сервісом можна користуватися з 13 років."
        override val checkoutConsent = "Покупки — лише для дорослих (18+). Оплату проводить Paddle. Оплачуючи, ви приймаєте Умови використання та Політику повернень."
        override val backToSite = "← На сайт"
    }

    override val access = object : AccessStrings {
        override val badge = "Закрите тестування"
        override val title = "Створюй ігри для Roblox за допомогою ШІ"
        override val subtitle = "Опиши ідею словами — Craftiva AI напише Luau-скрипт, зробить 3D-модель або анімацію та надішле все в Roblox Studio. Зараз ми тестуємо сервіс з невеликою групою користувачів."
        override val f1Title = "Скрипти на Luau"
        override val f1Text = "Двері, зброя, магазини, системи шкоди — готовий перевірений код за твоїм описом."
        override val f2Title = "3D-моделі"
        override val f2Text = "Намалюй концепт або опиши предмет — отримаєш модель, готову для Roblox."
        override val f3Title = "Просто в Studio"
        override val f3Text = "Плагін Craftiva переносить результат у твою гру одним кліком."
        override val formTitle = "Запросити доступ"
        override val formHint = "Залиш заявку — ми відкриємо доступ і надішлемо лист, коли можна буде увійти."
        override val nameLabel = "Як тебе звати"
        override val namePlaceholder = "Ім'я або нік"
        override val messageLabel = "Що хочеш створювати"
        override val messagePlaceholder = "Наприклад: свою obby з пастками та магазином"
        override val ageConfirm = "Мені 13 років або більше (якщо менше 16 і я живу в ЄС — за згодою батьків)"
        override val privacyAccept = "Я приймаю Політику конфіденційності"
        override val submit = "Надіслати заявку"
        override val sentTitle = "Заявку надіслано!"
        override fun sentText(email: String) = "Ми напишемо на $email, щойно відкриємо доступ. Зазвичай це займає 1–2 дні."
        override val alreadyApproved = "Вже схвалили?"
        override val signIn = "Увійти"
        override val notApproved = "Для цього email доступ ще не відкрито. Залиш заявку на сторінці сервісу — ми надішлемо лист після схвалення."
        override val consentRequired = "Підтверди вік і згоду з політикою конфіденційності"
        override val sendFailed = "Не вдалося надіслати заявку. Спробуй ще раз."
        override val adminTab = "Заявки"
        override val pending = "Очікують"
        override val approved = "Схвалені"
        override val rejected = "Відхилені"
        override val all = "Усі"
        override val approve = "Схвалити"
        override val reject = "Відхилити"
        override val noRequests = "Заявок немає"
        override val approvedToast = "Доступ відкрито, лист надіслано"
        override val rejectedToast = "Заявку відхилено"
    }

    override val meshInspector = object : MeshInspectorStrings {
        override val reliefMap = "Карта рельєфу"
        override val reliefYes = "є — дрібні деталі на світлі"
        override val reliefNo = "немає"
        override val relief = "Рельєф"
        override val reliefHint = "Дрібні деталі детальної моделі, перенесені в текстуру (так їх покаже Roblox)"
        override fun number(value: Int) = groupDigits(value, " ")
        override val tabInfo = "Інфо"
        override val tabView = "Вигляд"
        override val tabTools = "Інструменти"
        override fun studs(value: Double): String {
            val n = shortDecimal(value, comma = true)
            return if (isWhole(value)) "$n ${slavicPlural(kotlin.math.round(value).toInt(), "стад", "стади", "стадів")}" else "$n стада"
        }
        override val readinessTitle = "Готовність до Roblox"
        override val readyAll = "Готово для Roblox"
        override fun readyIssues(count: Int) = "Є що виправити: $count"
        override fun checkTriangles(count: Int, max: Int) = "Трикутники: ${groupDigits(count, " ")} з ${groupDigits(max, " ")}"
        override val checkTrianglesOver = "Більше за ліміт MeshPart — Studio не прийме модель"
        override fun checkTexture(size: Int) = "Текстура ${size}×$size"
        override val checkTextureNone = "Без текстури — кольори зберігаються у вершинах"
        override val checkTextureOver = "Roblox зменшить текстуру до 1024 — краще стиснути заздалегідь"
        override val checkFrontAuto = "Перед моделі повернуто до Roblox-фронту (−Z) автоматично"
        override val checkFrontUser = "Перед моделі обрано тобою"
        override val checkFrontUnsure = "Невідомо, де в моделі перед — подивися вигляд «Спереду»"
        override fun checkSize(dimensions: String) = "Розмір: $dimensions"
        override val checkSizeTiny = "Дуже маленька — у грі її буде важко розгледіти"
        override val checkSizeHuge = "Дуже велика — перевір, що так і задумано"
        override val checkPartsOk = "Цілісна модель, без відірваних шматочків"
        override fun checkParts(small: Int) = "Відірвані шматочки: $small — схоже на сміття генерації"
        override val checkPivotOk = "Стоїть на підлозі, центр унизу"
        override val checkPivotOff = "Не стоїть на підлозі — у Studio модель зависне або потоне"
        override val checking = "Перевіряю…"
        override val fixSimplify = "Спростити"
        override val fixTexture = "Стиснути"
        override val fixRotate = "Повернути"
        override val fixSize = "Задати розмір"
        override val fixDebris = "Прибрати сміття"
        override val fixPivot = "Поставити на підлогу"
        override val geometry = "Геометрія"
        override val triangles = "Трикутники"
        override val vertices = "Вершини"
        override val parts = "Окремі частини"
        override val size = "Розмір"
        override val fileSize = "Файл"
        override fun megabytes(bytes: Long) = if (bytes < 1024 * 1024) "${shortDecimal(bytes / 1024.0, true)} КБ" else "${shortDecimal(bytes / 1048576.0, true)} МБ"
        override val texture = "Текстура"
        override fun pixels(size: Int) = "${size}×$size px"
        override val noTexture = "немає"
        override val origin = "Звідки модель"
        override val provider = "Генератор"
        override val created = "Створено"
        override val version = "Версія"
        override val sketch = "Ескіз"
        override val mode = "Режим"
        override val light = "Світло"
        override val background = "Тло"
        override val bgDark = "Темне"
        override val bgLight = "Світле"
        override val bgGrey = "Сіре"
        override val bgBaseplate = "Бейсплейт"
        override val reference = "Аватар R15 поруч"
        override val referenceHint = "Порівняй розмір моделі зі звичайним персонажем Roblox (5,2 стада)"
        override val bounds = "Габарити в стадах"
        override val grid = "Сітка підлоги"
        override val spin = "Обертання"
        override val screenshot = "Знімок"
        override val screenshotTransparent = "Без тла (PNG)"
        override val video = "Відео обертання"
        override val videoRecording = "Записую відео…"
        override val videoUnsupported = "Цей браузер не вміє записувати відео"
        override val toolsNote = "Кожна зміна зберігається новою версією — до старої можна повернутися."
        override val budget = "Кількість трикутників"
        override val budgetMobile = "Телефони"
        override val budgetNormal = "Звичайно"
        override val budgetDetailed = "Детально"
        override fun trianglesValue(count: Int) = "${groupDigits(count, " ")} трик."
        override val apply = "Застосувати"
        override val sizeTitle = "Розмір у стадах"
        override val sizeHint = "Найдовша сторона моделі"
        override val rotateTitle = "Повернути перед"
        override val rotateHint = "Якщо модель стоїть у Studio боком або спиною"
        override val rotateLeft = "↺ 90°"
        override val rotateRight = "↻ 90°"
        override val rotate180 = "180°"
        override val textureTitle = "Розмір текстури"
        override val debrisTitle = "Прибрати відірвані шматочки"
        override val debrisHint = "Дрібні частини в повітрі, які залишив генератор"
        override val downloadTitle = "Завантажити"
        override val downloadGlb = "GLB"
        override val downloadObj = "OBJ + текстура"
        override val versions = "Версії"
        override fun versionLabel(number: Int) = "v$number"
        override val current = "зараз"
        override val restore = "Повернути"
        override val compare = "Порівняти"
        override val closeCompare = "Закрити порівняння"
        override val originalVersion = "Як згенеровано"
        override fun editTriangles(count: Int) = "сітка на ${groupDigits(count, " ")} трик."
        override val editHighPoly = "детальна модель (High-Poly)"
        override fun budgetFromSource(sourceTriangles: Int?) = "Детальна модель генератора${sourceTriangles?.let { " (" + groupDigits(it, " ") + " трик.)" } ?: ""} зберігається 30 днів після створення: можна і зменшити, і збільшити кількість трикутників."
        override val downloadHighPoly = "High-Poly (GLB)"
        override val downloadSharp = "GLB · текстура 2K"
        override val rigTitle = "Скелет для анімації"
        override val rigHint = "Ставить у модель 15 кісток як у R15 (безкоштовно). Найкраще для чоловічків, що стоять прямо. Потім обери модель як персонажа в налаштуваннях анімації."
        override val rigApply = "Поставити кістки"
        override val rigDone = "Кістки поставлено: модель можна обрати як персонажа анімації."
        override val rigDownload = "GLB зі скелетом"
        override val rigWorking = "Ставлю кістки…"
        override fun rigPro(cost: String?) = "Точніше — Meshy" + (cost?.let { " · $it" } ?: "")
        override val rigProHint = "Meshy ставить 24 кістки й акуратніше розподіляє шкіру; лише для чоловічків, близько хвилини."
        override val rigByMeshy = "Кістки від Meshy"
        override val preparingSharp = "Готую модель з текстурою 2K — це триватиме до хвилини."
        override val retextureTitle = "Змінити текстуру"
        override val retextureHint = "Форма й розмір залишаться, зміняться кольори та матеріал. Триватиме 1–3 хвилини, вийде нова версія. Опиши стиль або додай картинку-зразок."
        override val retexturePresets = listOf("Золото", "Камінь", "Дерево", "Метал та іржа", "Мультяшний, яскраві кольори", "Лід")
        override val retexturePlaceholder = "Наприклад: лицарські обладунки із золота з червоними вставками"
        override val retexturePicture = "Картинка-зразок"
        override val retextureRemovePicture = "Прибрати картинку"
        override fun retextureApply(cost: String?) = "Перефарбувати" + (cost?.let { " · $it" } ?: "")
        override val retextureWorking = "Перефарбовую модель — це триватиме 1–3 хвилини"
        override val editRetexture = "нова текстура"
        override fun editSize(studs: String) = "розмір $studs"
        override fun editRotate(degrees: Int) = "поворот на $degrees°"
        override fun editTexture(size: Int) = "текстура $size px"
        override val editDebris = "прибрано сміття"
        override val working = "Змінюю модель… це триватиме до хвилини"
        override fun saved(number: Int) = "Готово — збережено як версію $number"
        override val failed = "Не вдалося змінити модель. Спробуй ще раз."
        override val unavailable = "Інструменти для моделей на цьому сервері ще не під'єднано"
    }

    override val animationSetup = object : AnimationSetupStrings {
        override val characterTitle = "Персонаж"
        override val characterR15 = "Стандартний R15"
        override val characterR15Hint = "звичайний аватар Roblox"
        override val characterCustom = "Свій персонаж"
        override val characterCustomHint = "Свій персонаж: відкрий 3D-роботу → «Інструменти» → «Поставити кістки»."
        override val characterOwnHint = "моя 3D-робота з кістками"
        override val modeTitle = "Режим"
        override val modeNone = "Звичайний"
        override val modeNoneHint = "танці, ходьба, емоції"
        override val modeFists = "Кулаки"
        override val modeFistsHint = "удари, блоки, ухиляння"
        override val modeOne = "Одна зброя"
        override val modeOneHint = "меч, гармата, посох, сокира"
        override val modeTwo = "Дві зброї"
        override val modeTwoHint = "комбо двома руками"
        override val weaponTitle = "Зброя (права рука)"
        override val offhandTitle = "Друга рука"
        override val sword = "Меч"
        override val gun = "Гармата"
        override val staff = "Посох"
        override val axe = "Сокира"
        override fun weaponHint(weapon: String) = when (weapon) { "GUN" -> "приціл і віддача"; "STAFF" -> "довгі махи"; "AXE" -> "важка, із замахом"; else -> "швидкі удари" }
        override val weaponModelTitle = "Модель зброї"
        override val weaponModelBuiltIn = "Вбудована"
        override val weaponModelBuiltInHint = "проста форма"
        override val weaponModelOwnHint = "моя 3D-робота"
        override val gripTitle = "Хват"
        override val gripOne = "Однією рукою"
        override val gripOneHint = "друга рука вільна"
        override val gripTwo = "Двома руками"
        override val gripTwoHint = "обидві руки на руків'ї"
        override val note = "Рухи враховують вагу зброї: важка — із замахом та інерцією. Сама зброя в руці з'явиться в прев'ю пізніше."
        override val noteWithWeapon = "Рухи враховують вагу зброї: важка — із замахом та інерцією. У прев’ю зброя в руці; у Studio плагін прикріпить до руки заготовку."
        override val openWorkspace = "Робоча область"
        override val closeWorkspace = "Закрити"
        override val clipTitle = "Кліп"
        override val duration = "Тривалість"
        override val looped = "по колу"
        override val once = "один раз"
        override val keys = "Ключі"
        override val jointsTitle = "Суглоби зараз"
        override val prevKey = "Попередній ключ"
        override val nextKey = "Наступний ключ"
        override val zoomIn = "Наблизити"
        override val zoomOut = "Віддалити"
        override val resetCamera = "Скинути камеру"
        override val timeZoom = "Масштаб часу"
        override val cameraHint = "Колесо — зум · ліва кнопка — обертання · права або Shift — зсув"
        override val head = "Голова"
        override val torso = "Корпус"
        override val leftArm = "Ліва рука"
        override val rightArm = "Права рука"
        override val leftLeg = "Ліва нога"
        override val rightLeg = "Права нога"
        override val editTitle = "Доопрацювати анімацію"
        override val editHint = "Опиши, що змінити, — решта залишиться як є. Кожна правка зберігається версією."
        override val editPresets = listOf("Рухи рук ширші", "Швидше", "Повільніше", "Плавніше", "Стрибок вищий", "Швидше повернутися у вихідну позу")
        override val editPlaceholder = "Наприклад: зроби змах меча різкішим і з більшим замахом"
        override fun editApply(cost: String?) = "Змінити" + (cost?.let { " · $it" } ?: "")
        override val editWorking = "Змінюю анімацію…"
        override fun editDone(version: Int) = "Готово: версія $version"
        override val editFailed = "Не вдалося змінити"
        override val versionsTitle = "Версії"
        override val versionOriginal = "початкова"
        override val restore = "Повернути"
        override val libraryOpen = "Вибрати модель…"
        override val libraryTitle = "Модель персонажа і зброї"
        override val libraryDone = "Готово"
        override val libraryCharacterHint = "Персонажу потрібні кістки — завантаженій моделі вони ставляться самі. Свою модель можна завантажити нижче."
        override val libraryWeaponHint = "Модель, яку персонаж тримає в руці. Вбудована — меч, гармата, посох або сокира з режиму."
        override val libraryNoBones = "без кісток"
        override val libraryEmpty = "Тут з’являться твої 3D-роботи й завантажені моделі."
        override val libraryAddBones = "Поставити кістки (безкоштовно)"
        override val libraryAddingBones = "Ставлю кістки…"
        override val libraryTools = "Інструменти моделі"
        override val libraryDownload = "Завантажити .glb"
        override val libraryUpload = "Завантажити свою модель"
        override val libraryUploadHint = ".glb або .obj разом із .mtl і текстурами, до 30 МБ. Натисни або перетягни файли сюди."
        override val libraryUploading = "Завантажую й готую модель…"
        override val libraryFailed = "Не вдалося"
        override fun libraryImported(name: String) = "Модель «$name» завантажено"
        override val weaponTitleOne = "Зброя — вбудована або з «Моїх робіт»"
        override val rightHandTitle = "Права рука"
        override val leftHandTitle = "Ліва рука"
        override val handTitle = "Рука"
        override val handLeadTitle = "Провідна рука"
        override val handRight = "Права"
        override val handLeft = "Ліва"
        override val handRightHint = "Зброя в правій руці"
        override val handLeftHint = "Зброя в лівій руці: рухи віддзеркалюються"
        override fun weaponMovesLike(kind: String) = "рухається як ${kind.lowercase()}"
    }

    override val quality = object : QualityStrings {
        override val sketchTitle = "Ескіз"
        override val meshTitle = "3D-модель"
        override val sketchStandard = "Швидкий"
        override val sketchStandardHint = "дешево, але може домалювати зайве"
        override val sketchHigh = "Точний"
        override val sketchHighHint = "набагато краще дотримується опису"
        override val meshStandard = "Звичайна"
        override val meshStandardHint = "≈30 с, для спроби й простих речей"
        override val meshHigh = "Висока"
        override val meshHighHint = "≈1 хв, чистіша форма й текстура"
        override val meshMax = "Максимальна"
        override val meshMaxHint = "≈3 хв, найбільше деталей"
        override val note = "Що вища якість, то більше кредитів. Обирай під задачу — для спроби вистачить звичайної."
        override fun approve(cost: String) = "Зробити 3D · $cost"
        override val drewCheaper = "Точна модель ескізів зараз недоступна — намалювала швидка, списано ціну швидкого ескізу"
        override val variantsTitle = "Скільки ескізів"
        override val variantsOne = "1"
        override val variantsOneHint = "якщо ідея вже зрозуміла"
        override val variantsFour = "4 на вибір"
        override val variantsFourHint = "малюються одразу, обираєш найкращий"
        override val pickVariant = "Натисни на ескіз, з якого робити 3D"
        override fun variantLabel(number: Int) = "Варіант $number"
        override val trianglesTitle = "Полігони (трикутники)"
        override val trianglesMob = "Моб / декор"
        override val trianglesCharacter = "Гравець / персонаж"
        override val trianglesBoss = "Бос / великий об'єкт"
        override val trianglesHighPoly = "High-Poly"
        override val trianglesHighPolyHint = "без ліміту, для завантаження; у Roblox — до 20 000"
        override val trianglesCustom = "Своє число"
        override fun trianglesUpTo(count: Int) = "до ${groupDigits(count, " ")}"
        override val trianglesNote = "Roblox MeshPart приймає до 20 000 трикутників. Більше трикутників — більше дрібних деталей, але модель важча для гри."
        override val textureTitle = "Текстура"
        override val texture1kHint = "1024 px — як у Roblox"
        override val texture2kHint = "2048 px — чіткіше в завантаженій моделі"
        override val texture4kHint = "4096 px — у «Високої», в High-Poly моделі"
        override val textureNote = "У Roblox завжди йде текстура 1024 px — Studio сам зменшує більші. 2K і 4K потрібні для завантаження та інших програм."
    }

    override val accountData = object : AccountDataStrings {
        override val title = "Твої дані"
        override val hint = "Завантаж усе, що сервіс зберігає про тебе, або видали акаунт назавжди."
        override val download = "Завантажити мої дані"
        override val delete = "Видалити акаунт"
        override val deleteTitle = "Видалити акаунт назавжди?"
        override val deleteWarning = "Буде видалено акаунт, усі твої роботи, їхні версії, 3D-моделі та ескізи. Невикористані кредити згорять. Скасувати це не можна."
        override val confirmLabel = "Введи свій email для підтвердження"
        override val confirmButton = "Видалити назавжди"
        override val emailMismatch = "Email не збігається з акаунтом"
        override val subscriptionActive = "Спершу скасуй підписку на сторінці «Тарифи», потім видали акаунт."
        override val deleted = "Акаунт видалено"
        override val failed = "Не вдалося видалити акаунт. Спробуй ще раз."
        override val adminDelete = "Видалити акаунт"
        override val adminDeleted = "Акаунт видалено"
        override fun adminConfirm(email: String) = "Видалити акаунт $email з усіма роботами? Скасувати не можна."
    }
}
